import {
  Component,
  ChangeDetectionStrategy,
  Input,
  EventEmitter,
  Output,
  OnChanges,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { map } from 'rxjs/operators';
import { TuiDialogContext, TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusContent } from '@taiga-ui/polymorpheus';
import { NgxPermissionsService } from 'ngx-permissions';
import { TranslateService } from '@ngx-translate/core';
import { BasicGroupFullInfoUnion, ChatMember, ChatMemberUnion, SupergroupFullInfoUnion } from '@airgram/web';
import { TelegramAuthService, TelegramMessengerService } from '@src/app/modules/telegram';
import { GroupUI, ViewMode, UserUI } from '@src/models';
import { SubscriptionsForUsersService, SubscriptionsForUsersCommitteeService, UserService } from '@src/core/services';
import { APP_CONFIG } from '@src/core';
import { UsersSearchParameters } from '@src/api';
import { BaseResizableComponent } from '@src/app/modules/base';
import { PhotoService } from '@src/app/modules/photo';

import { GroupInfoService } from './index';

@Component({
  selector: 'app-group-info',
  templateUrl: './group-info.component.html',
  styleUrls: ['./group-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupInfoComponent extends BaseResizableComponent implements OnChanges {
  private readonly messengerService = inject(TelegramMessengerService);
  private readonly groupInfoService = inject(GroupInfoService);
  private readonly userService = inject(UserService);
  private readonly ngxPermissionsService = inject(NgxPermissionsService);
  private readonly subscriptionsForUsersService = inject(SubscriptionsForUsersService);
  private readonly subscriptionsForUsersCommitteeService = inject(SubscriptionsForUsersCommitteeService);
  private readonly telegramAuthService = inject(TelegramAuthService);
  private readonly translateService = inject(TranslateService);
  private readonly photoService = inject(PhotoService);
  private readonly dialogService = inject(TuiDialogService);

  @Input() mode: ViewMode = 'view';
  @Output() modeChange = new EventEmitter<ViewMode>();

  @Input() type?: 'organisation' | 'committee';
  @Input() data?: GroupUI;
  @Input() isChannel = false;
  @Input() organisationId?: string;
  @Input() initFilter?: UsersSearchParameters;
  @Input() allowSubscribing?: boolean | null = false;
  @Input() allowSpecialFieldsViewing?: boolean | null = false;
  @Input() loading = false;
  @Output() create = new EventEmitter<GroupUI>();
  @Output() edit = new EventEmitter<GroupUI>();
  @Output() saved = new EventEmitter<GroupUI>();
  @Output() canceled = new EventEmitter<void>();
  @Output() deleted = new EventEmitter<string>();
  @ViewChild('confirmDialogTemplate') confirmDialogTemplate?: PolymorpheusContent<TuiDialogContext>;

  membersLoading = false;
  allowEditing$?: Observable<boolean | undefined>;
  allowDeleting$?: Observable<boolean | undefined>;
  allowDeletingFromTelegram = false;
  authUser?: UserUI;
  group?: SupergroupFullInfoUnion | BasicGroupFullInfoUnion;
  groupId?: number;
  members?: ChatMemberUnion[];
  memberCount?: number;
  deleteFromTelegram = false;

  override ngOnInit(): void {
    super.ngOnInit();

    this.telegramAuthService.isLogged$.subscribe(isLogged => {
      if (isLogged) {
        this.allowDeletingFromTelegram = this.userService.authUser?.id === this.data?.ownerId;
        this.loadMembers().finally(() => (this.membersLoading = false));
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data) {
      this.groupId = undefined;
      this.group = undefined;
      this.members = undefined;

      if (!!this.telegramAuthService.isLogged$.value) {
        this.loadMembers().finally(() => (this.membersLoading = false));
      }
    }

    if (changes.isChannel) {
      this.data = {
        ...this.data,
        isChannel: this.isChannel,
      };
    }

    if (changes.mode) {
      if (this.mode === 'create') {
        this.data = {
          isChannel: this.isChannel,
          organisationId: this.organisationId,
        };
      }
    }

    this.allowEditing$ = this.userService.authUser$.pipe(
      map(user => {
        this.authUser = user;

        if (!user || !user.organisationId) return false;

        const permissions = this.ngxPermissionsService.getPermissions();
        const permissionsForOrganisation = this.data?.organisationId
          ? user.permissionsForOrganisations?.[this.data?.organisationId]
          : [];

        // TODO: refactoring
        return (
          ((this.data && user.id === this.data.ownerId) || this.mode === 'create') &&
          ('organisationChatsEditing' in permissions ||
            permissionsForOrganisation?.includes('onlyYourOrganisationChatsEditing'))
        );
      }),
    );

    this.allowDeleting$ = this.userService.authUser$.pipe(
      map(user => {
        this.authUser = user;

        if (!user) return false;

        const permissions = this.ngxPermissionsService.getPermissions();

        return 'organisationChatsEditing' in permissions;
      }),
    );

    this.cdr.markForCheck();
  }

  async loadMembers() {
    const chatId = this.data?.chatId;

    if (this.membersLoading || !chatId || !this.data?.active) {
      return;
    }

    this.membersLoading = true;

    await this.messengerService.getAllChatIds();

    return this.messengerService.api.getChat(chatId).then(chat => {
      if (chat.type._ === 'chatTypeBasicGroup') {
        this.groupId = chat.type.basicGroupId;
        this.isChannel = false;
        this.messengerService.api.getBasicGroupFullInfo(this.groupId).then(basicGroup => {
          this.group = basicGroup;
          this.getMembers(basicGroup, this.groupId, chatId);
        });
      }

      if (chat.type._ === 'chatTypeSupergroup') {
        this.groupId = chat.type.supergroupId;
        this.isChannel = chat.type.isChannel;
        this.messengerService.api.getSupergroupFullInfo(this.groupId).then(superGroup => {
          this.group = superGroup;
          this.memberCount = superGroup.memberCount;
          this.getMembers(superGroup, this.groupId, chatId);
        });
      }
    });
  }

  refreshMembers() {
    this.getMembers(this.group, this.groupId, this.data?.chatId);
  }

  //TODO: Избавиться от параметра chatId. Реализовать метод отмены предыдущего запроса
  async getMembers(group?: SupergroupFullInfoUnion | BasicGroupFullInfoUnion, id?: number, chatId?: number) {
    let members: ChatMember[] = [];

    if (group?._ === 'basicGroupFullInfo' && id) {
      members = group.members;
    }

    if (group?._ === 'supergroupFullInfo' && group.canGetMembers && id) {
      const superGroupMembers = await this.messengerService.api.getSupergroupMembers(id).catch(() => undefined);
      if (!superGroupMembers) {
        this.allowDeletingFromTelegram = false;
        this.membersLoading = false;
        this.cdr.markForCheck();
        return;
      }

      members = superGroupMembers.members;

      const limit = APP_CONFIG.loadingLimit.chatMembers;
      if (group.memberCount > limit) {
        for (let i = 1; i < Math.ceil(group.memberCount / limit); i++) {
          const updatedSupergroupMembers = await this.messengerService.api.getSupergroupMembers(id, limit * i, limit);
          members = [...new Set(members.concat(...updatedSupergroupMembers.members))];
        }
      }
    }

    if (this.data?.chatId === chatId) {
      this.members = members;
    }

    this.membersLoading = false;
    this.cdr.markForCheck();
  }

  async onSaveData(data: GroupUI): Promise<void> {
    if (!data || !data.name) return;

    if (this.mode === 'create') {
      this.create.emit(data);
    } else if (this.mode === 'edit') {
      this.edit.emit(data);
    }
  }

  applyDelete(observer: Observer<void>): void {
    observer.complete();

    if (this.deleteFromTelegram && this.data?.chatId) {
      this.groupInfoService.deleteGroup(this.data.chatId).then(res => {
        if (res) {
          this.deleted.emit();
        }
      });
    } else {
      this.deleted.emit();
    }
  }

  onCancel(): void {
    if (this.mode === 'edit') {
      this.setMode('view');
    }
    this.canceled.emit();
  }

  onStartEditing(): void {
    this.setMode('edit');
  }

  async onDeletePhoto(): Promise<void> {
    if (this.data?.photoId) {
      this.photoService.deletePhoto(this.data.photoId);
    }

    if (this.data?.chatId) {
      await this.messengerService.api.deleteChatPhoto(this.data.chatId);
    }
  }

  onSubscribe(): void {
    if (!this.data?.id || !this.data.chatId || !this.data?.codeName) return;

    if (this.type === 'organisation') {
      this.subscriptionsForUsersService.subscribeChannel(this.data.id, this.data.chatId, this.data.codeName);
    }

    if (this.type === 'committee') {
      this.subscriptionsForUsersCommitteeService.subscribeGroup(this.data.id, this.data.chatId, this.data.codeName);
    }
  }

  onUnsubscribe(): void {
    if (!this.data?.id || !this.data?.chatId || !this.authUser?.telegramId) return;

    if (this.type === 'organisation') {
      this.subscriptionsForUsersService.unsubscribeChannel(this.data.id, this.data.chatId, this.authUser.telegramId);
    }

    if (this.type === 'committee') {
      this.subscriptionsForUsersCommitteeService.unsubscribeGroup(
        this.data.id,
        this.data.chatId,
        this.authUser.telegramId,
      );
    }
  }

  openConfirmDialog(): void {
    if (!this.confirmDialogTemplate) return;

    this.deleteFromTelegram = false;

    this.dialogService
      .open(this.confirmDialogTemplate, {
        label: this.isChannel
          ? this.translateService.instant('components.groupInfo.dialogs.channelDeleteHeader')
          : this.translateService.instant('components.groupInfo.dialogs.groupDeleteHeader'),
        size: 's',
        closeable: false,
        dismissible: false,
      })
      .subscribe();
  }

  setMode(mode: ViewMode) {
    this.mode = mode;
    this.modeChange.emit(mode);
  }
}
